import React, { useState, useCallback, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react'
import Building from '../../entities/Building'
import { Overlay3D } from './Overlay'
import { ApartmentFootprint } from '../Engine/types'
import { initialUnits, mainApartments } from './UnitList'

type BuildingViewerProps = {
  building: Building;
  frameCount?: number;
  quadrantSize?: number;
  tiltAngle?: number;
  getFrameUrl?: (idx: number) => string;
  onApartmentSelected?: (apartment: any) => void;
  hideUIOnRotation?: boolean;
  selectedApartment?: any;
  hoveredApartment?: { id: string; floor: number } | null;
};

const BuildingViewer = forwardRef<any, BuildingViewerProps>(({
  building,
  frameCount = 119,
  getFrameUrl = (idx) => `/images/image_${idx}.webp`,
  onApartmentSelected,
  hideUIOnRotation = false,
  selectedApartment,
  hoveredApartment,
}, ref) => {
  const {
    rotationY,
    frameIndex,
    isRotating,
    nextFrame,
    prevFrame,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  } = useCubeRotation(frameCount)

  // Image preloader state - MOVED UP before useEffects
  const [isLoading, setIsLoading] = useState(true)
  const [loadedCount, setLoadedCount] = useState(0)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const imageCacheRef = useRef<HTMLImageElement[]>([])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextFrame()
      if (e.key === 'ArrowLeft') prevFrame()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [nextFrame, prevFrame])

  // Preload ALL images with progress tracking
  useEffect(() => {
    // Only run once - prevent re-loading
    if (hasLoadedOnce) return

    const total = frameCount
    let loaded = 0
    imageCacheRef.current = []
    setLoadedCount(0)
    setIsLoading(true)

    const loadImage = (index: number) => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          loaded++
          setLoadedCount(loaded)
          if (loaded === total) {
            setIsLoading(false)
            setHasLoadedOnce(true)
          }
          resolve(img)
        }
        img.onerror = () => {
          loaded++
          setLoadedCount(loaded)
          if (loaded === total) {
            setIsLoading(false)
            setHasLoadedOnce(true)
          }
          reject(new Error(`Failed to load image ${index}`))
        }
        img.src = getFrameUrl(index)
      })
    }

    // Load all images simultaneously
    const loadAllImages = async () => {
      const promises = []
      for (let i = 0; i < total; i++) {
        promises.push(loadImage(i))
      }

      try {
        const images = await Promise.allSettled(promises)
        imageCacheRef.current = images
          .filter(result => result.status === 'fulfilled')
          .map(result => (result as PromiseFulfilledResult<HTMLImageElement>).value)
      } catch (error) {
        // Error loading building frames - handle silently
      }
    }

    loadAllImages()
  }, [frameCount, getFrameUrl, hasLoadedOnce])

  // Track selected apartment to preserve across hiding/showing
  const [selectedApt, setSelectedApt] = useState<{ aptId: string; floorIndex: number } | null>(null)

  // Update selectedApt when selectedApartment prop changes (from parent)
  useEffect(() => {
    if (selectedApartment && selectedApartment.id) {
      // Convert selectedApartment to the format expected by Overlay3D
      setSelectedApt({
        aptId: selectedApartment.id,
        floorIndex: selectedApartment.floor - 1 // Convert floor number to floorIndex (floor 2 = floorIndex 1)
      })
    } else {
      setSelectedApt(null)
    }
  }, [selectedApartment])

  // Memoize building floors to prevent re-rendering
  const memoizedFloors = useMemo(() => building.floors, [building.floors])

  // Ref to communicate with Overlay3D
  const overlayRef = useRef<any>(null)

  // Called by Overlay3D whenever an apartment is clicked
  const handleApartmentClick = useCallback(
    (info: { aptId: string; floorIndex: number }) => {
      // Update selected apartment state
      setSelectedApt(info)

      // Find the apartment from the building data and create a detailed footprint
      const floor = building.floors[info.floorIndex]
      const apartment = floor?.apartments.find((apt) => apt.id === info.aptId)

      if (apartment) {
        // Find the apartment data from the unit list
        const unitData = initialUnits.find(unit =>
          unit.id.startsWith(apartment.id) && unit.floor === (info.floorIndex + 1)
        )

        // Find the apartment price and image from mainApartments
        const aptData = mainApartments.find(apt => apt.id === apartment.id)

        // Create a detailed apartment footprint using real data
        const fullApartmentId = `Apartment ${apartment.id}-${info.floorIndex + 1}`
        const detailedApartment: ApartmentFootprint = {
          id: apartment.id,
          name: fullApartmentId,
          outline: apartment.outline,
          holes: apartment.holes,
          floor: info.floorIndex + 1,
          area: unitData?.area || aptData?.area || 85.5,
          price: aptData?.price,
          apt_plans: aptData?.apt_plans,
          rooms: Array.from({ length: unitData?.rooms || 2 }, (_, index) => {
            const roomTypes = ['living', 'bedroom', 'kitchen', 'bathroom']
            const roomNames = ['Living Room', 'Bedroom', 'Kitchen', 'Bathroom']
            return {
              id: (index + 1).toString(),
              name: roomNames[index % roomNames.length],
              type: roomTypes[index % roomTypes.length] as 'living' | 'bedroom' | 'kitchen' | 'bathroom',
              size: Math.round((unitData?.area || 85.5) / (unitData?.rooms || 2)),
              floor: info.floorIndex + 1,
              windowCount: index === 0 ? 2 : 1,
              hasBalcony: index === 0,
              amenities: index === 0 ? ['Air Conditioning', 'Hardwood Floors'] :
                index === 1 ? ['Built-in Closet'] :
                  index === 2 ? ['Modern Appliances'] : ['Modern Fixtures'],
              description: `${roomNames[index % roomNames.length].toLowerCase()} with great features`,
              panoramaNode: []
            }
          }),
          topdownImageUrl: '/apartment_footprint.png'
        }

        // Notify parent component
        onApartmentSelected?.(detailedApartment)
      }
    },
    [building, onApartmentSelected]
  )

  // Mobile rotation functions (2 frames at a time)
  const mobileRotateLeft = useCallback(() => {
    nextFrame()
    nextFrame()
  }, [nextFrame])

  const mobileRotateRight = useCallback(() => {
    prevFrame()
    prevFrame()
  }, [prevFrame])

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    deselectApartment: () => {
      setSelectedApt(null)
      if (overlayRef.current) {
        overlayRef.current.deselectApartment()
      }
    }
  }), [])
  // ─────────────────────────────────────────────────────────────────────

  const progressPercentage = Math.round((loadedCount / frameCount) * 100)

  return (
    <div
      className="relative flex justify-center items-center h-full w-full rounded-2xl overflow-hidden select-none cursor-grab"
      onMouseDown={(e) => handleDragStart(e.clientX)}
      onMouseMove={(e) => handleDragMove(e.clientX)}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
      onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
      onTouchEnd={handleDragEnd}
    >
      {isLoading && (
        /* Building Viewer Loading Screen */
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-gray-100 rounded-2xl">
          {/* Circular Progress */}
          <div className="relative w-24 h-24 mb-6">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              {/* Background Circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="#e5e7eb"
                strokeWidth="8"
                fill="none"
              />
              {/* Progress Circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="#3b82f6"
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - progressPercentage / 100)}`}
                className="transition-all duration-200 ease-out"
              />
            </svg>
            {/* Percentage Text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xl font-bold text-gray-700">{progressPercentage}%</span>
            </div>
          </div>

          {/* Loading Text and Counter */}
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Loading Building...</h3>
            <p className="text-sm text-gray-600">
              {loadedCount} of {frameCount} frames loaded
            </p>
            <div className="mt-4 flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      )}
      {/* ─── Your 360° frame-based background ─── */}
      <div className="absolute w-full h-full overflow-hidden flex items-center justify-center">
        <img
          src={getFrameUrl(frameIndex)}
          alt={`Frame ${frameIndex}`}
          className="absolute w-full mx-auto h-full object-contain transform scale-[2] brightness-110 flex-shrink-0"
          // className="absolute w-full mx-auto h-full min-w-[620px] object-contain transform scale-[1] brightness-150"
          draggable={false}
        />
      </div>

      {/* ─── The 3D Overlay ─── */}
      {(!hideUIOnRotation || !isRotating) && (
        <Overlay3D
          ref={overlayRef}
          key="building-overlay"
          floorsData={memoizedFloors}
          rotationDeg={rotationY + 40}
          //rotationDeg={rotationY + 0}
          frameIndex={frameIndex}
          onApartmentClick={handleApartmentClick}
          selectedApartment={selectedApt}
          hoveredApartment={hoveredApartment}
        />
      )}


      {/* Mobile Rotation Controls */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-4 hidden">
        <button
          onClick={mobileRotateLeft}
          className="bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105"
          aria-label="Rotate left"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          onClick={mobileRotateRight}
          className="bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105"
          aria-label="Rotate right"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  )
})

function useCubeRotation (frameCount: number, dragSpeed: number = 0.25) {
  const [rotationY, setRotationY] = useState(0)
  const [isRotating, setIsRotating] = useState(false)
  const [opacity, setOpacity] = useState(0.3)
  const stepAngle = 360 / frameCount
  const frameIndex = Math.round(rotationY / stepAngle) % frameCount
  const timeoutRef = useRef<number>()
  const [hasDragged, setHasDragged] = useState(false)
  const dragStartPos = useRef({ x: 0, y: 0 })

  const updateRotationState = useCallback(() => {
    if (!isRotating) {
      setIsRotating(true)
      setOpacity(0)
    }
    window.clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => {
      setIsRotating(false)
      setOpacity(0.3)
    }, 150) // Shorter timeout for better responsiveness
  }, [isRotating])

  const nextFrame = useCallback(() => {
    setRotationY((r) => (r + stepAngle) % 360)
    updateRotationState()
  }, [stepAngle, updateRotationState])

  const prevFrame = useCallback(() => {
    setRotationY((r) => (r - stepAngle + 360) % 360)
    updateRotationState()
  }, [stepAngle, updateRotationState])

  // Drag state
  const [isDragging, setIsDragging] = useState(false)
  const lastXRef = useRef(0)

  const handleDragStart = useCallback(
    (clientX: number) => {
      setIsDragging(true)
      setHasDragged(false)
      lastXRef.current = clientX
      dragStartPos.current = { x: clientX, y: 0 }
      // Don't immediately set rotation state
    },
    []
  )

  const handleDragMove = useCallback(
    (clientX: number) => {
      if (!isDragging) return

      const deltaX = clientX - lastXRef.current
      const totalDragDistance = Math.abs(clientX - dragStartPos.current.x)

      // Only start rotation if user has dragged more than 5 pixels
      if (totalDragDistance > 5) {
        if (!hasDragged) {
          setHasDragged(true)
          updateRotationState()
        }
        lastXRef.current = clientX
        setRotationY((r) => (r - deltaX * dragSpeed + 360) % 360)
        updateRotationState()
      }
    },
    [isDragging, dragSpeed, updateRotationState, hasDragged]
  )

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)

    // If user didn't drag (just clicked), immediately show overlay
    if (!hasDragged) {
      setIsRotating(false)
      setOpacity(0.3)
    } else {
      // If user dragged, wait a bit before showing overlay
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => {
        setIsRotating(false)
        setOpacity(0.3)
      }, 100)
    }
    setHasDragged(false)
  }, [hasDragged])

  useEffect(() => {
    return () => window.clearTimeout(timeoutRef.current)
  }, [])

  return {
    rotationY,
    frameIndex,
    isRotating,
    opacity,
    nextFrame,
    prevFrame,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  }
}

export default React.memo(BuildingViewer)
